import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { CartItem, Product } from "@shared/api";

interface CartContextValue {
  items: CartItem[];
  totalQuantity: number;
  isOpen: boolean;
  cartIconRef: React.RefObject<HTMLButtonElement>;
  add: (
    product: Product,
    quantity?: number,
    origin?: DOMRect | null,
    imageSrc?: string,
  ) => void;
  remove: (id: string) => void;
  increment: (id: string) => void;
  decrement: (id: string) => void;
  clear: () => void;
  open: () => void;
  close: () => void;
  toggle: () => void;
}

const CartContext = createContext<CartContextValue | null>(null);
const STORAGE_KEY = "fusion.cart";

interface FlyItem {
  id: string;
  src: string;
  start: { x: number; y: number; w: number; h: number };
  end: { x: number; y: number };
}

export function CartProvider({ children }: { children: React.ReactNode }) {
  const [items, setItems] = useState<CartItem[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const cartIconRef = useRef<HTMLButtonElement>(null);
  const [flies, setFlies] = useState<FlyItem[]>([]);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) setItems(JSON.parse(raw));
    } catch {}
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch {}
  }, [items]);

  const totalQuantity = useMemo(
    () => items.reduce((s, it) => s + it.quantity, 0),
    [items],
  );

  const add = useCallback(
    (
      product: Product,
      quantity = 1,
      origin?: DOMRect | null,
      imageSrc?: string,
    ) => {
      setItems((prev) => {
        const existing = prev.find((i) => i.id === product.id);
        if (existing) {
          return prev.map((i) =>
            i.id === product.id ? { ...i, quantity: i.quantity + quantity } : i,
          );
        }

        return [
          ...prev,
          {
            id: product.id,
            name: product.name,
            price: product.price,
            image: product.image,
            quantity,
          },
        ];
      });

      if (origin && imageSrc && cartIconRef.current) {
        const target = cartIconRef.current.getBoundingClientRect();
        const fly: FlyItem = {
          id: `${Date.now()}-${Math.random()}`,
          src: imageSrc,
          start: {
            x: origin.left,
            y: origin.top,
            w: origin.width,
            h: origin.height,
          },
          end: {
            x: target.left + target.width / 2,
            y: target.top + target.height / 2,
          },
        };
        setFlies((f) => [...f, fly]);
      }

      // Ensure the cart drawer is visible when an item is added
      setIsOpen(true);
    },
    [],
  );

  const remove = useCallback(
    (id: string) => setItems((p) => p.filter((i) => i.id !== id)),
    [],
  );
  const increment = useCallback(
    (id: string) =>
      setItems((p) =>
        p.map((i) => (i.id === id ? { ...i, quantity: i.quantity + 1 } : i)),
      ),
    [],
  );
  const decrement = useCallback(
    (id: string) =>
      setItems((p) =>
        p.map((i) =>
          i.id === id ? { ...i, quantity: Math.max(1, i.quantity - 1) } : i,
        ),
      ),
    [],
  );
  const clear = useCallback(() => setItems([]), []);
  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => setIsOpen(false), []);
  const toggle = useCallback(() => setIsOpen((v) => !v), []);

  return (
    <CartContext.Provider
      value={{
        items,
        totalQuantity,
        isOpen,
        cartIconRef,
        add,
        remove,
        increment,
        decrement,
        clear,
        open,
        close,
        toggle,
      }}
    >
      {children}
      <FlyLayer
        items={flies}
        onDone={(id) => setFlies((f) => f.filter((x) => x.id !== id))}
      />
    </CartContext.Provider>
  );
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used within CartProvider");
  return ctx;
}

function FlyLayer({
  items,
  onDone,
}: {
  items: FlyItem[];
  onDone: (id: string) => void;
}) {
  return (
    <div className="pointer-events-none fixed inset-0 z-[60]">
      {items.map((it) => (
        <Fly key={it.id} item={it} onDone={() => onDone(it.id)} />
      ))}
    </div>
  );
}

function Fly({ item, onDone }: { item: FlyItem; onDone: () => void }) {
  const ref = useRef<HTMLImageElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const startX = item.start.x;
    const startY = item.start.y;
    const dx = item.end.x - (item.start.x + item.start.w / 2);
    const dy = item.end.y - (item.start.y + item.start.h / 2);
    el.style.left = `${startX}px`;
    el.style.top = `${startY}px`;
    el.style.width = `${item.start.w}px`;
    el.style.height = `${item.start.h}px`;
    requestAnimationFrame(() => {
      el.style.transform = `translate(${dx}px, ${dy}px) scale(0.2)`;
      el.style.opacity = "0.7";
    });
    const done = () => onDone();
    el.addEventListener("transitionend", done, { once: true });
    return () => el.removeEventListener("transitionend", done);
  }, [item, onDone]);
  return (
    <img
      ref={ref}
      src={item.src}
      alt=""
      style={{
        position: "fixed",
        transition:
          "transform 600ms cubic-bezier(0.22, 1, 0.36, 1), opacity 600ms",
        willChange: "transform",
        borderRadius: 8,
        boxShadow: "0 0 30px hsla(160,100%,50%,0.4)",
      }}
    />
  );
}
